import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, Save, X, Search, ChevronRight, ChevronDown, DownloadCloud } from 'lucide-react';
import { API_URL } from '../../config';

export default function IndustryLibraryConfig() {
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIndustry, setSelectedIndustry] = useState(null);

  // For new/edit industry
  const [showIndModal, setShowIndModal] = useState(false);
  const [editingInd, setEditingInd] = useState(null);
  const [indForm, setIndForm] = useState({ name: '', description: '' });

  // For new/edit theme
  const [showThemeModal, setShowThemeModal] = useState(false);
  const [editingTheme, setEditingTheme] = useState(null);
  const [themeForm, setThemeForm] = useState({ themeName: '', keywordsStr: '', description: '' });

  useEffect(() => {
    fetchIndustries();
  }, []);

  const fetchIndustries = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}${'/industry-library'}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }).then(r => r.json());
      setIndustries(res);
      if (res.length > 0 && !selectedIndustry) {
        setSelectedIndustry(res[0]);
      } else if (selectedIndustry) {
        // refresh selected
        const updatedSelected = res.find(i => i.id === selectedIndustry.id);
        setSelectedIndustry(updatedSelected || res[0]);
      }
    } catch (err) {
      console.error(err);
      alert('加载行业库失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveInd = async () => {
    if (!indForm.name.trim()) return alert('行业名称不能为空');
    try {
      if (editingInd) {
        await fetch(`${API_URL}${`/industry-library/${editingInd.id}`}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` }, body: JSON.stringify(indForm) }).then(r => r.json());
      } else {
        await fetch(`${API_URL}${'/industry-library'}`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` }, body: JSON.stringify(indForm) }).then(r => r.json());
      }
      setShowIndModal(false);
      fetchIndustries();
    } catch (err) {
      console.error(err);
      alert('保存失败: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteInd = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('确定要删除该行业吗？该行业下的所有预置维度也将被删除！')) return;
    try {
      await fetch(`${API_URL}${`/industry-library/${id}`}`, { method: 'DELETE', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }).then(r => r.json());
      if (selectedIndustry?.id === id) setSelectedIndustry(null);
      fetchIndustries();
    } catch (err) {
      alert('删除失败');
    }
  };

  const handleSaveTheme = async () => {
    if (!themeForm.themeName.trim()) return alert('维度名称不能为空');
    if (!selectedIndustry) return alert('未选择行业');

    const keywordsArray = themeForm.keywordsStr.split(/[\n,，]+/).map(k => k.trim()).filter(k => k);
    
    try {
      if (editingTheme) {
        await api.put(`/industry-library/themes/${editingTheme.id}`, {
          themeName: themeForm.themeName,
          description: themeForm.description,
          keywords: keywordsArray
        });
      } else {
        await api.post(`/industry-library/${selectedIndustry.id}/themes`, {
          themeName: themeForm.themeName,
          description: themeForm.description,
          keywords: keywordsArray
        });
      }
      setShowThemeModal(false);
      fetchIndustries();
    } catch (err) {
      console.error(err);
      alert('保存失败: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteTheme = async (themeId) => {
    if (!window.confirm('确定要删除该维度吗？')) return;
    try {
      await fetch(`${API_URL}${`/industry-library/themes/${themeId}`}`, { method: 'DELETE', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }).then(r => r.json());
      fetchIndustries();
    } catch (err) {
      alert('删除失败');
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto min-h-[calc(100vh-64px)]">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">全行业预置黄金词库</h1>
          <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
            动态维护全行业核心业务维度与拓词种子库，创建品牌项目时 AI 将以此为基础执行精准分发。
          </p>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-6">
        {/* Left: Industry List */}
        <div className="w-full md:w-1/4 bg-white dark:bg-slate-800/50 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-700/50 flex flex-col min-h-[600px]">
          <div className="p-4 border-b border-gray-100 dark:border-slate-700/50 flex justify-between items-center bg-gray-50/50 dark:bg-slate-900/20">
            <h2 className="font-semibold text-gray-700 dark:text-slate-200">行业板块分类</h2>
            <button 
              onClick={() => { setEditingInd(null); setIndForm({ name: '', description: '' }); setShowIndModal(true); }}
              className="p-1.5 text-indigo-600 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-900/30 rounded-md transition-colors"
            >
              <Plus size={18} />
            </button>
          </div>
          <div className="overflow-y-auto flex-1 p-2 space-y-1 custom-scrollbar">
            {loading ? (
              <div className="p-4 text-center text-sm text-gray-500">加载中...</div>
            ) : industries.map(ind => (
              <div 
                key={ind.id}
                onClick={() => setSelectedIndustry(ind)}
                className={`group flex items-center justify-between p-3 rounded-xl cursor-pointer transition-colors ${
                  selectedIndustry?.id === ind.id 
                    ? 'bg-indigo-50 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-500/30' 
                    : 'text-gray-600 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800 border border-transparent'
                }`}
              >
                <span className="font-medium text-sm truncate pr-2">{ind.name}</span>
                <div className="flex opacity-0 group-hover:opacity-100 transition-opacity">
                  <button 
                    onClick={(e) => { e.stopPropagation(); setEditingInd(ind); setIndForm({ name: ind.name, description: ind.description || '' }); setShowIndModal(true); }}
                    className="p-1 text-gray-400 hover:text-indigo-500 rounded"
                  >
                    <Edit2 size={14} />
                  </button>
                  <button 
                    onClick={(e) => handleDeleteInd(ind.id, e)}
                    className="p-1 text-gray-400 hover:text-red-500 rounded"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Themes & Keywords */}
        <div className="w-full md:w-3/4 bg-white dark:bg-slate-800/50 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-700/50 flex flex-col">
          {selectedIndustry ? (
            <>
              <div className="p-5 border-b border-gray-100 dark:border-slate-700/50 flex justify-between items-center bg-gray-50/50 dark:bg-slate-900/20">
                <div>
                  <h2 className="font-bold text-lg text-gray-800 dark:text-slate-100 flex items-center gap-2">
                    {selectedIndustry.name} 
                    <span className="text-xs font-normal bg-gray-200 dark:bg-slate-700 text-gray-600 dark:text-slate-300 px-2 py-0.5 rounded-full">
                      {selectedIndustry.themes.length} 个维度
                    </span>
                  </h2>
                  <p className="text-xs text-gray-500 mt-1">{selectedIndustry.description || '无行业描述'}</p>
                </div>
                <button 
                  onClick={() => { setEditingTheme(null); setThemeForm({ themeName: '', keywordsStr: '', description: '' }); setShowThemeModal(true); }}
                  className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-colors text-sm font-medium shadow-sm"
                >
                  <Plus size={16} /> 新增维度字典
                </button>
              </div>
              <div className="p-6 flex-1 overflow-y-auto custom-scrollbar bg-slate-50/30 dark:bg-slate-900/10">
                {selectedIndustry.themes.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-gray-400 py-12">
                    <DownloadCloud size={48} className="mb-4 text-gray-300 dark:text-slate-600" />
                    <p>该行业下暂无预置维度，请点击右上角新增</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                    {selectedIndustry.themes.map(theme => (
                      <div key={theme.id} className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-4 hover:shadow-md transition-shadow group relative">
                        <div className="flex justify-between items-start mb-3 border-b border-gray-100 dark:border-slate-700/50 pb-3">
                          <div>
                            <h3 className="font-bold text-gray-800 dark:text-slate-200 flex items-center gap-2">
                              {theme.themeName}
                              <span className="text-[10px] bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-400 px-1.5 py-0.5 rounded">
                                {theme.keywords.length} 词
                              </span>
                            </h3>
                            {theme.description && <p className="text-xs text-gray-500 mt-1">{theme.description}</p>}
                          </div>
                          <div className="flex opacity-0 group-hover:opacity-100 transition-opacity">
                            <button 
                              onClick={() => { setEditingTheme(theme); setThemeForm({ themeName: theme.themeName, description: theme.description || '', keywordsStr: theme.keywords.join('\n') }); setShowThemeModal(true); }}
                              className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-500/20 rounded-md transition-colors"
                            >
                              <Edit2 size={14} />
                            </button>
                            <button 
                              onClick={() => handleDeleteTheme(theme.id)}
                              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors ml-1"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto custom-scrollbar pr-1">
                          {theme.keywords.map((kw, idx) => (
                            <span key={idx} className="bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300 text-xs px-2 py-1 rounded-md border border-gray-200/50 dark:border-slate-600/50">
                              {kw}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <p>请在左侧选择或创建一个行业</p>
            </div>
          )}
        </div>
      </div>

      {/* Industry Modal */}
      {showIndModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-md p-6 border border-gray-100 dark:border-slate-700">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">{editingInd ? '编辑行业' : '新增行业'}</h3>
              <button onClick={() => setShowIndModal(false)} className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"><X size={20} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">行业名称 <span className="text-red-500">*</span></label>
                <input 
                  type="text" 
                  value={indForm.name}
                  onChange={(e) => setIndForm({...indForm, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-slate-900 dark:text-white outline-none"
                  placeholder="例如: 美妆护肤"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">描述 (可选)</label>
                <textarea 
                  value={indForm.description}
                  onChange={(e) => setIndForm({...indForm, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-slate-900 dark:text-white outline-none resize-none"
                  rows={3}
                  placeholder="该行业的大致特征或目标客群说明..."
                />
              </div>
            </div>
            <div className="mt-8 flex justify-end gap-3">
              <button onClick={() => setShowIndModal(false)} className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600 rounded-lg text-sm font-medium transition-colors">取消</button>
              <button onClick={handleSaveInd} className="px-4 py-2 text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg text-sm font-medium shadow-sm transition-colors flex items-center gap-2">
                <Save size={16} /> 保存
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Theme Modal */}
      {showThemeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-2xl p-6 border border-gray-100 dark:border-slate-700 flex flex-col max-h-[90vh]">
            <div className="flex justify-between items-center mb-6 shrink-0">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                {editingTheme ? '编辑维度字典' : '新增维度字典'} <span className="text-sm font-normal text-gray-500">({selectedIndustry?.name})</span>
              </h3>
              <button onClick={() => setShowThemeModal(false)} className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"><X size={20} /></button>
            </div>
            <div className="space-y-4 overflow-y-auto flex-1 pr-2 custom-scrollbar">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">维度名称 <span className="text-red-500">*</span></label>
                <input 
                  type="text" 
                  value={themeForm.themeName}
                  onChange={(e) => setThemeForm({...themeForm, themeName: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-slate-900 dark:text-white outline-none"
                  placeholder="例如: 核心抗老"
                />
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">预置黄金词 (批量录入) <span className="text-red-500">*</span></label>
                  <span className="text-xs text-gray-500">支持逗号、换行分隔</span>
                </div>
                <textarea 
                  value={themeForm.keywordsStr}
                  onChange={(e) => setThemeForm({...themeForm, keywordsStr: e.target.value})}
                  className="w-full px-3 py-3 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-slate-900 dark:text-white outline-none font-mono text-sm leading-relaxed"
                  rows={10}
                  placeholder="抗初老\n淡纹\n紧致\n抗皱\n垮脸\n..."
                />
                <div className="mt-1 text-xs text-gray-500 flex justify-between">
                  <span>不限词量，直接从 Excel 复制粘贴即可。</span>
                  <span className="text-indigo-600 dark:text-indigo-400 font-medium">当前有效词汇: {themeForm.keywordsStr.split(/[\n,，]+/).filter(k=>k.trim()).length} 个</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">内部备注</label>
                <input 
                  type="text" 
                  value={themeForm.description}
                  onChange={(e) => setThemeForm({...themeForm, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-slate-900 dark:text-white outline-none"
                  placeholder="仅内部可见..."
                />
              </div>
            </div>
            <div className="mt-6 pt-4 border-t border-gray-100 dark:border-slate-700 shrink-0 flex justify-end gap-3">
              <button onClick={() => setShowThemeModal(false)} className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600 rounded-lg text-sm font-medium transition-colors">取消</button>
              <button onClick={handleSaveTheme} className="px-4 py-2 text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg text-sm font-medium shadow-sm transition-colors flex items-center gap-2">
                <Save size={16} /> 保存维度库
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
